import React from 'react';

import { ThemeProvider as EmotionThemeProvider } from '@emotion/react';
import { Trans } from '@lingui/react/macro';
import { StyledEngineProvider, ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import { AudioRecorderProvider } from '@repo/videa-notes';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConnectedRouter } from 'connected-react-router/immutable';
// @ts-expect-error TS(7016): Could not find a declaration file for module 'hist... Remove this comment to see the full error message
import { createBrowserHistory } from 'history';
import 'jotai-devtools/styles.css';
import PropTypes from 'prop-types';
import { Provider } from 'react-redux';
import { Route, Router, Switch } from 'react-router';

import { DebugTools } from '@/common-components/debug/DebugTools';
import { useTrackActiveRoute } from '@/modules/videaNotes/hooks/useTrackActiveRoute';
import { VideaNotesSheet } from '@/modules/videaNotes/VideaNotesSheet';

import WithAxios from '../common-components/axios/WithAxios';
import oktaConfig from '../oktaConstants';
import RoutesContainer from '../routes/RoutesContainer';
import { theme } from '../theme/theme';
import { useToggleDevTools } from '../utils/hooks/useDevtoolsShortcut';

import LoginCallback from './LoginCallback';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const HashHistoryRoot = (props: any) => {
  useToggleDevTools();
  useTrackActiveRoute();
  const { history } = props;
  return (
    <ConnectedRouter history={history}>
      <RoutesContainer history={history} />
    </ConnectedRouter>
  );
};

const Root = (props: any) => {
  const { store, history } = props;

  const restoreOriginalUri = async (_oktaAuth: any, originalUri: any) => {
    history.replace(toRelativeUrl(originalUri || '/', window.location.origin));
  };

  const oktaAuth = new OktaAuth(oktaConfig.oidc);
  const browserHistory = createBrowserHistory();

  // special route for okta login callback since `#` is not allowed
  // see [sample](https://github.com/okta/okta-react/blob/master/samples/routing/react-router-dom-v5-hash)
  return (
    <>
      <DebugTools />
      <QueryClientProvider client={queryClient}>
        <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={theme}>
                <EmotionThemeProvider theme={theme}>
                  <Provider store={store}>
                    <AudioRecorderProvider>
                      <Router history={browserHistory}>
                        <WithAxios>
                          <>
                            <Switch>
                              <Route
                                path="/login/callback"
                                render={(lcprops) => (
                                  <LoginCallback
                                    {...lcprops}
                                    homePath="/"
                                    loadingElement={
                                      <p id="login-callback-loading">
                                        <Trans>Loading...</Trans>
                                      </p>
                                    }
                                  />
                                )}
                              />

                              <Route
                                component={() => (
                                  <HashHistoryRoot history={history} />
                                )}
                              />
                            </Switch>
                            <VideaNotesSheet />
                          </>
                        </WithAxios>
                      </Router>
                    </AudioRecorderProvider>
                  </Provider>
                </EmotionThemeProvider>
              </ThemeProvider>
            </StyledEngineProvider>
          </LocalizationProvider>
        </Security>
      </QueryClientProvider>
    </>
  );
};

Root.propTypes = {
  store: PropTypes.object,
  history: PropTypes.object,
};

export default Root;
